import { InlineAlertType, InlineAlertWidget } from '@tableau/widgets';
import React from 'react';
import { SagaIterator } from 'redux-saga';
import { call, put, takeEvery } from 'redux-saga/effects';
import _ from 'underscore';

import { ResourceGovernanceEditSite, ResourceGovernanceResetSiteToDefaults } from '~src/app/actions/AppActionTypes';
import { requestFailed } from '~src/app/actions/RequestFailedAction';
import { Messages } from '~src/codegen/Localize';
import { openReactConfirmationDialog } from '~src/commonInfra/dialogs/ReactConfirmationDialog';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { WebHttpClientWithErrorToastInstance } from '~src/httpClient/WebHttpClientWithErrorToast';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import { ToasterService, ToastType } from '~src/ts/services/ToasterService';

import { ResourceGovernanceEditSiteAction } from '../actions/ResourceGovernanceEditSiteAction';
import { ResourceGovernanceResetSiteToDefaultsAction } from '../actions/ResourceGovernanceResetSiteToDefaultAction';
import { openResourceGovernanceEditSiteLimitsDialog, ResourceGovernanceEditSiteLimitsDialogProps, SiteLimitsErrorLinkToken } from '../components/ResourceGovernanceEditSiteLimitsDialog';
import { GetResourceGovernanceServerSettingsRequest, IGetResourceGovernanceServerSettingsResult, IResourceGovernanceSiteSetting, IResourceGovernanceSiteSettingUpdate, isResourceGovernanceSettingLower, UpdateResourceGovernanceSiteSettingsRequest } from '../types/ResourceGovernance';

export function* editSiteLimit(action: ResourceGovernanceEditSiteAction) {
  try {
    let defaultLimits: IGetResourceGovernanceServerSettingsResult = yield call(getGovernanceServerSettings);
    if (defaultLimits && defaultLimits.errors?.length) {
      yield put(requestFailed('Failed to get default limits', Messages.errorUnknown()));
      return;
    }
    let props: ResourceGovernanceEditSiteLimitsDialogProps = {
      sitesSettingsList: action.settings,
      serverSettings: defaultLimits,
    };
    yield call(openResourceGovernanceEditSiteLimitsDialog, props);
    yield put(requestSpaceContents());
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export async function openResetSiteLimitDialog(siteIds: string[], settings: IResourceGovernanceSiteSetting[]): Promise<IResult> {
  const defaultLimits: IGetResourceGovernanceServerSettingsResult = await getGovernanceServerSettings();
  const shouldAlert = _.any(settings, setting => isResourceGovernanceSettingLower(setting, defaultLimits));
  const dialog = openReactConfirmationDialog({
    title: Messages.resourceGovernanceResetSiteToDefaultsTitle(),
    message: Messages.resourceGovernanceResetSiteToDefaultsMessage(),
    actionName: Messages.confirm(),
    action: siteIds => {
      const updateParams: IResourceGovernanceSiteSettingUpdate = {
        siteIds,
        isDefault: true,
        // None of the other fields matter
        extractsConcurrencyLimit: null,
        subscriptionsConcurrencyLimit: null,
        flowsConcurrencyLimit: null,
        runtimeLimit: null,
      };
      return WebHttpClientWithErrorToastInstance.sendRequest(
        new UpdateResourceGovernanceSiteSettingsRequest(updateParams));
    },
    paramsForAction: siteIds,
    dialogWidth: 500,
    bodyComponent: () => <>
      {Messages.resourceGovernanceResetSiteToDefaultsMessage()}
      {shouldAlert && <InlineAlertWidget
        testId='decreased-limit-alert'
        type={InlineAlertType.Warning}
      >
        {Messages.resourceGovernanceConcurrencyDecreasedToDefaultLimitAlert()}
      </InlineAlertWidget>}
    </>,
  });
  const result = await dialog.result;
  if (result.errors?.length > 0) {
    ToasterService.getInstance().pop({
      type: ToastType.Error,
      message: Messages.resourceGovernanceResetSiteToDefaultsError(),
      actionMessage: Messages.resourceGovernanceLearnMoreMessage(),
      actionHref: HelpService.getServerHelpUrlByCategory(SiteLimitsErrorLinkToken),
    });
  } else {
    ToasterService.getInstance().pop({
      type: ToastType.Success,
      message: Messages.resourceGovernanceResetSiteToDefaultsSuccess(),
    });
  }
  return result;
}

export function* resetSiteLimit(action: ResourceGovernanceResetSiteToDefaultsAction) {
  try {
    const confirmDialog: IResult = yield call(openResetSiteLimitDialog, action.siteIds, action.settings);
    yield put(requestSpaceContents());
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* rootResourceGovernanceSaga(): SagaIterator {
  yield takeEvery(ResourceGovernanceEditSite, editSiteLimit);
  yield takeEvery(ResourceGovernanceResetSiteToDefaults, resetSiteLimit);
}

export function getGovernanceServerSettings(): Promise<IGetResourceGovernanceServerSettingsResult> {
  return WebHttpClientWithErrorToastInstance.sendRequest(
    new GetResourceGovernanceServerSettingsRequest());
}
